/**
 * 簡易予測 (WMA系) の Entry 既定値取得 hook。
 *
 * GetSimpleForecastEntryDefaults RPC を呼んで、加重移動平均(短期) / 同区分加重平均の
 * 設定ダイアログ向け Entry 行 (取込元日付 / 日タイプ / 祝日名 / 既定重み) を取得する。
 * dates が空の場合は backend が既定の Entry 群を導出し (初期表示)、
 * dates を指定すると日タイプ再解決のみを行う (Entry 日付変更時の再判定用)。
 * enabled=false（既定）のため、caller が true に変更するまで fetch しない。
 */
import { create } from '@bufbuild/protobuf'
import type { MessageShape } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { AggregationScope } from '@ebalance/proto/bffuser/v1/demand_confirm_record_model_pb'
import { getSimpleForecastEntryDefaults } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import type { SimpleForecastMethod } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import {
  GetSimpleForecastEntryDefaultsRequestSchema,
  GetSimpleForecastEntryDefaultsResponseSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

type Params = {
  caseId: string | undefined
  /** 業務エリアコード */
  areaCode: AreaCode | undefined
  /** 予測対象日 */
  targetDate: ProtoDate | undefined
  /** 簡易予測手法 (WMA 系のみ意味を持つ) */
  method: SimpleForecastMethod | undefined
  targetScope: 'BG_TOTAL' | 'RETAILER' | 'UNATTRIBUTED'
  retailerBusinessCd: string | undefined
  /**
   * 既に選択済みの取込元日付群。空 = 既定 Entry 導出 (初期表示)、
   * 指定あり = その日付群の日タイプ解決のみ (Entry 日付変更時の再判定用)。
   */
  dates?: ProtoDate[]
  /** true のときのみ fetch を実行する */
  enabled?: boolean
}

/**
 * WMA 設定ダイアログの Entry 既定値を取得する。
 * enabled=false（既定）のため、caller が true に変更するまで fetch しない。
 *
 * fail-safe: 既定値取得の失敗 (取込元候補不足などの業務エラー・技術失敗) は
 * 設定ダイアログ上部の Alert に原因別 / 汎用文言で表示する (issue #6956)。
 * error boundary (全画面エラー) に飛ばさないため、global QueryClient の
 * `throwOnError: true` を本 query だけ `false` に上書きする
 * (docs/guides/frontend-coding-guide.yaml「queries は throwOnError: true」からの意図的な逸脱)。
 */
export function useGetSimpleForecastEntryDefaults({
  caseId,
  areaCode,
  targetDate,
  method,
  targetScope,
  retailerBusinessCd,
  dates = [],
  enabled = false,
}: Params): UseQueryResult<
  MessageShape<typeof GetSimpleForecastEntryDefaultsResponseSchema>,
  ConnectError
> {
  const canFetch =
    enabled &&
    areaCode !== undefined &&
    targetDate !== undefined &&
    method !== undefined

  return useQuery(
    getSimpleForecastEntryDefaults,
    create(GetSimpleForecastEntryDefaultsRequestSchema, {
      areaCode: areaCode ?? 0,
      targetDate,
      method: method ?? 0,
      dates,
      caseId,
      scope:
        targetScope === 'BG_TOTAL'
          ? AggregationScope.BG_TOTAL
          : AggregationScope.RETAIL,
      retailerBusinessCd:
        targetScope === 'RETAILER' ? retailerBusinessCd : undefined,
    }),
    {
      enabled: canFetch,
      staleTime: 0,
      throwOnError: false,
    },
  )
}
